import { useState, useEffect } from 'react';
import { Form } from 'antd';
import { ImageTags } from '@/constants/CompressorImageTagData.const';
import { COMPRESSOR_DATA, ROOMS } from '@/store/SiteSurveyForm/constants';
import { CompressorRowProps } from './CompressorRow.utils';

type SpecDataState = 'none' | 'card' | 'url-modal' | 'manual-modal';

export const useCompressorRow = ({
  compressor,
  roomId,
  roomIndex,
  index,
  pressureType,
  onRemoveCompressor,
  onUpdateManualCompressorField,
  onEditCompressorImageTag,
  onOpenCompressorImageUpload,
  onRemoveCompressorImage,
}: Pick<
  CompressorRowProps,
  | 'compressor'
  | 'roomId'
  | 'roomIndex'
  | 'index'
  | 'pressureType'
  | 'onRemoveCompressor'
  | 'onUpdateManualCompressorField'
  | 'onEditCompressorImageTag'
  | 'onOpenCompressorImageUpload'
  | 'onRemoveCompressorImage'
>) => {
  const form = Form.useFormInstance();
  const [isVisible, setIsVisible] = useState(false);
  const [currentImageIndex, setCurrentImageIndex] = useState(0);
  const [specDataState, setSpecDataState] = useState<SpecDataState>('none');

  const additionalInformation = Form.useWatch([
    pressureType,
    ROOMS,
    roomIndex,
    COMPRESSOR_DATA,
    index,
    'additionalInformation',
  ]);

  const hasAdditionalInformation = () => {
    if (!additionalInformation) return false;

    const { platform, cooling, pressure, horsepower, specUrl } = additionalInformation;
    return !!(platform || cooling || pressure || horsepower || specUrl);
  };

  const handleNoMatchingSpecSelect = () => {
    if (!hasAdditionalInformation()) {
      setSpecDataState('url-modal');
    }
  };

  const handleAdditionalInfoSave = () => {
    setSpecDataState('card');
  };

  const handleEditAdditionalInfo = (hasUrl?: boolean) => {
    const urlExists =
      hasUrl ??
      !!form.getFieldValue([
        pressureType,
        ROOMS,
        roomIndex,
        COMPRESSOR_DATA,
        index,
        'additionalInformation',
        'specUrl',
      ]);

    setSpecDataState(urlExists ? 'url-modal' : 'manual-modal');
  };

  const handleModalClose = () => {
    setSpecDataState(hasAdditionalInformation() ? 'card' : 'none');
  };

  const openCompressorImageUpload = () => onOpenCompressorImageUpload(compressor.id, compressor.images?.length || 0);

  const removeCompressor = () => onRemoveCompressor(roomId, index);

  const openEditPreview = (imageIndex: number) => {
    setIsVisible(true);
    setCurrentImageIndex(imageIndex);
  };

  const removeCompressorImage = (id: number, tag: ImageTags) => onRemoveCompressorImage(roomId, index, tag, id);

  const editCompressorImageTag = (id: number, newTag: ImageTags, oldTag: ImageTags) =>
    onEditCompressorImageTag(roomId, index, id, newTag, oldTag);

  const closePreview = () => setIsVisible(false);

  const updateManualCompressorField = (field: string, value: string) => {
    if (compressor.isManual) {
      onUpdateManualCompressorField(roomId, index, field, value);
    }
  };

  useEffect(() => {
    if (!compressor.images?.length) {
      setIsVisible(false);
    } else if (currentImageIndex >= compressor.images.length) {
      setCurrentImageIndex(compressor.images.length - 1);
    }
  }, [compressor.images, currentImageIndex]);

  return {
    isVisible,
    currentImageIndex,
    specDataState,

    hasAdditionalInformation,

    handleNoMatchingSpecSelect,
    handleAdditionalInfoSave,
    handleEditAdditionalInfo,
    handleModalClose,
    openCompressorImageUpload,
    removeCompressor,
    openEditPreview,
    removeCompressorImage,
    editCompressorImageTag,
    closePreview,
    updateManualCompressorField,

    setCurrentImageIndex,
  };
};
